import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { fetchDocuments } from "../services/firestoreDataService";
import { trackRead } from "../services/monitorService";
import {
  SCHEMA_FIELD_TYPES,
  ISSUE_LABELS,
  getSchema,
  saveSchema,
  deleteSchema,
  inferSchemaFromRows,
  scanDocuments,
} from "../services/schemaGuardService";

const EMPTY_SCHEMA = { fields: [], allowExtra: true };

export default function SchemaGuardPage() {
  const { collectionName, selectCollection, allCollections, can, Permission, user } = useFirestoreAdmin();
  const canEdit = can(Permission.edit);

  const [schema, setSchema] = useState(EMPTY_SCHEMA);
  const [sampleSize, setSampleSize] = useState(250);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [loadedRows, setLoadedRows] = useState([]);

  useEffect(() => {
    setSchema(getSchema(collectionName) || EMPTY_SCHEMA);
    setResult(null);
    setLoadedRows([]);
  }, [collectionName]);

  const loadSample = useCallback(async () => {
    if (!collectionName) return [];
    const res = await fetchDocuments({
      collectionName,
      pageLimit: Number(sampleSize) || 250,
      actorId: user?.uid || user?.email || null,
    });
    trackRead(res.rows.length);
    setLoadedRows(res.rows);
    return res.rows;
  }, [collectionName, sampleSize, user?.email, user?.uid]);

  const handleInfer = async () => {
    setBusy(true);
    try {
      const rows = await loadSample();
      if (!rows.length) {
        toast.error("No documents found to infer from");
        return;
      }
      setSchema(inferSchemaFromRows(rows));
      toast.success(`Inferred schema from ${rows.length} documents`);
    } catch (e) {
      toast.error(e?.message || "Failed to load sample");
    } finally {
      setBusy(false);
    }
  };

  const handleScan = async () => {
    if (!schema.fields.length) {
      toast.error("Define at least one field, or infer a schema first");
      return;
    }
    setBusy(true);
    try {
      const rows = loadedRows.length ? loadedRows : await loadSample();
      if (!rows.length) {
        toast.error("No documents found to scan");
        return;
      }
      setResult(scanDocuments(rows, schema));
    } catch (e) {
      toast.error(e?.message || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = () => {
    if (!collectionName) return;
    saveSchema(collectionName, schema);
    toast.success("Schema saved");
  };

  const handleDelete = () => {
    if (!collectionName) return;
    deleteSchema(collectionName);
    setSchema(EMPTY_SCHEMA);
    setResult(null);
    toast.success("Schema removed");
  };

  const updateField = (index, patch) => {
    setSchema((prev) => {
      const fields = prev.fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
      return { ...prev, fields };
    });
  };

  const addField = () => {
    setSchema((prev) => ({ ...prev, fields: [...prev.fields, { name: "", type: "string", required: false }] }));
  };

  const removeField = (index) => {
    setSchema((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Data Quality</p>
          <h1 className="text-2xl font-bold">Schema Guard</h1>
          <p className="text-sm text-slate-600">Define required fields &amp; types, then scan for documents that violate them.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={collectionName}
            onChange={(e) => selectCollection(e.target.value)}
          >
            {allCollections.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="number"
            min={10}
            max={1000}
            className="w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={sampleSize}
            onChange={(e) => setSampleSize(e.target.value)}
            title="Sample size"
          />
        </div>
      </header>

      <div className="fa-card space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">Schema definition</h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={schema.allowExtra !== false}
              onChange={(e) => setSchema((prev) => ({ ...prev, allowExtra: e.target.checked }))}
            />
            Allow fields not in schema
          </label>
        </div>

        <div className="fa-table-wrap">
          <table className="fa-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Required</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schema.fields.map((field, index) => (
                <tr key={index}>
                  <td>
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      placeholder="fieldName"
                      value={field.name}
                      onChange={(e) => updateField(index, { name: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      className="rounded border border-slate-300 px-2 py-1 text-xs"
                      value={field.type}
                      onChange={(e) => updateField(index, { type: e.target.value })}
                    >
                      {SCHEMA_FIELD_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!field.required}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                    />
                  </td>
                  <td>
                    <button type="button" className="fa-btn" onClick={() => removeField(index)}>Remove</button>
                  </td>
                </tr>
              ))}
              {!schema.fields.length && (
                <tr>
                  <td colSpan={4} className="text-center text-sm text-slate-500">
                    No fields defined. Add one manually or infer from a sample.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" className="fa-btn" onClick={addField}>Add field</button>
          <button type="button" className="fa-btn" onClick={handleInfer} disabled={busy || !collectionName}>
            Infer from sample
          </button>
          <button type="button" className="fa-btn fa-btn-primary" onClick={handleScan} disabled={busy || !collectionName}>
            {busy ? "Working…" : "Scan collection"}
          </button>
          {canEdit && (
            <>
              <button type="button" className="fa-btn" onClick={handleSave} disabled={!collectionName}>Save schema</button>
              <button type="button" className="fa-btn fa-btn-danger" onClick={handleDelete} disabled={!collectionName}>Delete schema</button>
            </>
          )}
        </div>
      </div>

      {result && (
        <>
          <div className="fa-kpi-grid">
            <div className="fa-kpi"><p className="fa-kpi-label">Scanned</p><p className="fa-kpi-value">{result.scanned}</p></div>
            <div className="fa-kpi"><p className="fa-kpi-label">Valid</p><p className="fa-kpi-value text-emerald-600">{result.valid}</p></div>
            <div className="fa-kpi"><p className="fa-kpi-label">With violations</p><p className="fa-kpi-value text-rose-600">{result.violations.length}</p></div>
            {Object.entries(result.issueCounts)
              .filter(([, n]) => n > 0)
              .map(([kind, n]) => (
                <div key={kind} className="fa-kpi">
                  <p className="fa-kpi-label">{ISSUE_LABELS[kind] || kind}</p>
                  <p className="fa-kpi-value">{n}</p>
                </div>
              ))}
          </div>

          <div className="fa-card fa-table-wrap">
            <table className="fa-table">
              <thead>
                <tr>
                  <th>Document ID</th>
                  <th>Field</th>
                  <th>Issue</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.violations.flatMap((v) =>
                  v.issues.map((issue, i) => (
                    <tr key={`${v.id}-${i}`}>
                      <td className="font-mono text-xs">{i === 0 ? v.id : ""}</td>
                      <td className="font-mono text-xs">{issue.field}</td>
                      <td className="text-xs">{ISSUE_LABELS[issue.kind] || issue.kind}</td>
                      <td className="text-xs text-slate-500">{issue.detail}</td>
                    </tr>
                  ))
                )}
                {!result.violations.length && (
                  <tr>
                    <td colSpan={4} className="text-center text-sm text-emerald-600">
                      No violations — all sampled documents match the schema.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
